import { useCallback } from 'react'

import { useModalContext } from '@sketch/components'
import {
  useGetProjectDetailsQuery,
  usePinProjectMutation,
  useUnpinProjectMutation,
  WorkspaceMinimalFragment,
} from '@sketch/gql-types'

import { useRegisterCommandBarCommands } from '@sketch/modules-common'
import { createCurrentFolderCommands } from './createCurrentFolderCommands'

interface CurrentFolderCommandBarRegistrationProps {
  workspace: WorkspaceMinimalFragment
  projectId: string
}

export const CurrentFolderCommandBarRegistration = ({
  workspace,
  projectId,
}: CurrentFolderCommandBarRegistrationProps) => {
  const { showModal } = useModalContext()

  const { data } = useGetProjectDetailsQuery({
    variables: { projectIdentifier: projectId },
    skip: !projectId,
    fetchPolicy: 'cache-only',
  })

  const project = data?.project
  const [pinProject] = usePinProjectMutation({
    onError: 'show-toast',
  })
  const [unpinProject] = useUnpinProjectMutation({
    onError: 'show-toast',
  })

  const toggleProjectPin = useCallback(() => {
    if (!project) {
      return
    }

    const variables = { projectIdentifier: project.identifier }

    if (project.pinnedByCurrentUserAt) {
      unpinProject({
        variables,
        optimisticResponse: {
          __typename: 'RootMutationType',
          unpinProject: {
            __typename: 'UnpinProjectResponse',
            project: {
              __typename: 'Project',
              identifier: project.identifier,
            },
          },
        },
      })
      return
    }

    pinProject({
      variables,
      optimisticResponse: {
        __typename: 'RootMutationType',
        pinProject: {
          __typename: 'PinProjectResponse',
          project: {
            __typename: 'Project',
            identifier: project.identifier,
          },
        },
      },
    })
  }, [pinProject, project, unpinProject])

  useRegisterCommandBarCommands(
    'current-folder',
    () =>
      project
        ? createCurrentFolderCommands({
            workspace,
            project,
            showModal,
            onTogglePin: toggleProjectPin,
          })
        : [],
    [workspace.identifier, workspace.name, project, toggleProjectPin]
  )

  return null
}
